const API_BASE = '/api/portfolios';

export const getPortfolios = async (authenticatedFetch) => {
  const response = await authenticatedFetch(API_BASE);
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ detail: 'Failed to fetch portfolios', code: 'INTERNAL_ERROR' }));
    const err = new Error(errorData.detail || 'Failed to fetch portfolios');
    err.code = errorData.code || null;
    throw err;
  }
  return response.json();
};

export const createPortfolio = async (authenticatedFetch, { name, description }) => {
  const response = await authenticatedFetch(API_BASE, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, description }),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ detail: 'Failed to create portfolio', code: 'INTERNAL_ERROR' }));
    const err = new Error(errorData.detail || 'Failed to create portfolio');
    err.code = errorData.code || null;
    throw err;
  }
  return response.json();
};

export const getPortfolioDetail = async (authenticatedFetch, portfolioId) => {
  const response = await authenticatedFetch(`${API_BASE}/${portfolioId}`);
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ detail: 'Failed to fetch portfolio', code: 'INTERNAL_ERROR' }));
    const err = new Error(errorData.detail || 'Failed to fetch portfolio');
    err.code = errorData.code || null;
    throw err;
  }
  return response.json();
};

export const updatePortfolio = async (authenticatedFetch, portfolioId, { name, description }) => {
  const response = await authenticatedFetch(`${API_BASE}/${portfolioId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, description }),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ detail: 'Failed to update portfolio', code: 'INTERNAL_ERROR' }));
    const err = new Error(errorData.detail || 'Failed to update portfolio');
    err.code = errorData.code || null;
    throw err;
  }
  return response.json();
};

export const deletePortfolio = async (authenticatedFetch, portfolioId) => {
  const response = await authenticatedFetch(`${API_BASE}/${portfolioId}`, {
    method: 'DELETE',
  });
  if (!response.ok && response.status !== 204) {
    const errorData = await response.json().catch(() => ({ detail: 'Failed to delete portfolio', code: 'INTERNAL_ERROR' }));
    const err = new Error(errorData.detail || 'Failed to delete portfolio');
    err.code = errorData.code || null;
    throw err;
  }
};

export const addHolding = async (authenticatedFetch, portfolioId, holdingData) => {
  const response = await authenticatedFetch(`${API_BASE}/${portfolioId}/holdings`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(holdingData),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ detail: 'Failed to add holding', code: 'INTERNAL_ERROR' }));
    const err = new Error(errorData.detail || 'Failed to add holding');
    err.code = errorData.code || null;
    throw err;
  }
  return response.json();
};

export const removeHolding = async (authenticatedFetch, portfolioId, holdingId) => {
  const response = await authenticatedFetch(`${API_BASE}/${portfolioId}/holdings/${holdingId}`, {
    method: 'DELETE',
  });
  if (!response.ok && response.status !== 204) {
    const errorData = await response.json().catch(() => ({ detail: 'Failed to remove holding', code: 'INTERNAL_ERROR' }));
    const err = new Error(errorData.detail || 'Failed to remove holding');
    err.code = errorData.code || null;
    throw err;
  }
};

export const getPortfolioAnalytics = async (authenticatedFetch, portfolioId) => {
  const response = await authenticatedFetch(`${API_BASE}/${portfolioId}/analytics`);
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ detail: 'Failed to fetch analytics', code: 'INTERNAL_ERROR' }));
    const err = new Error(errorData.detail || 'Failed to fetch analytics');
    err.code = errorData.code || null;
    throw err;
  }
  return response.json();
};

export const getPortfolioChart = async (authenticatedFetch, portfolioId, timeframe = '1Y') => {
  const response = await authenticatedFetch(`${API_BASE}/${portfolioId}/chart?timeframe=${timeframe}`);
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ detail: 'Failed to fetch chart data', code: 'INTERNAL_ERROR' }));
    const err = new Error(errorData.detail || 'Failed to fetch chart data');
    err.code = errorData.code || null;
    throw err;
  }
  return response.json();
};

export const getPortfolioInsights = async (authenticatedFetch, portfolioId) => {
  const response = await authenticatedFetch(`${API_BASE}/${portfolioId}/insights`);
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ detail: 'Failed to fetch insights', code: 'INTERNAL_ERROR' }));
    const err = new Error(errorData.detail || 'Failed to fetch insights');
    err.code = errorData.code || null;
    throw err;
  }
  return response.json();
};

export const assignPortfolioToClient = async (authenticatedFetch, portfolioId, clientId) => {
  const response = await authenticatedFetch(`/api/portfolios/${portfolioId}/client`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ client_id: clientId ?? null }),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ detail: 'Failed to assign portfolio', code: 'INTERNAL_ERROR' }));
    const err = new Error(errorData.detail || 'Failed to assign portfolio');
    err.code = errorData.code || null;
    throw err;
  }
  return response.json();
};

export const updateHolding = async (authenticatedFetch, portfolioId, holdingId, data) => {
  const response = await authenticatedFetch(`${API_BASE}/${portfolioId}/holdings/${holdingId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ detail: 'Failed to update holding', code: 'INTERNAL_ERROR' }));
    const err = new Error(errorData.detail || 'Failed to update holding');
    err.code = errorData.code || null;
    throw err;
  }
  return response.json();
};

export const getPortfolioInsurance = async (authenticatedFetch, portfolioId) => {
  const response = await authenticatedFetch(`${API_BASE}/${portfolioId}/insurance`);
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ detail: 'Failed to fetch insurance data', code: 'INTERNAL_ERROR' }));
    const err = new Error(errorData.detail || 'Failed to fetch insurance data');
    err.code = errorData.code || null;
    throw err;
  }
  return response.json();
};

export const revaluePortfolio = async (authenticatedFetch, portfolioId) => {
  const response = await authenticatedFetch(`${API_BASE}/${portfolioId}/revalue`, {
    method: 'POST',
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ detail: 'Failed to revalue portfolio', code: 'INTERNAL_ERROR' }));
    const err = new Error(errorData.detail || 'Failed to revalue portfolio');
    err.code = errorData.code || null;
    throw err;
  }
  return response.json();
};

export const getPortfolioBenchmark = async (authenticatedFetch, portfolioId) => {
  const response = await authenticatedFetch(`${API_BASE}/${portfolioId}/benchmark`);
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ detail: 'Failed to fetch benchmark data', code: 'INTERNAL_ERROR' }));
    const err = new Error(errorData.detail || 'Failed to fetch benchmark data');
    err.code = errorData.code || null;
    throw err;
  }
  return response.json();
};
